import React from 'react';
import { ReportField, ReportFieldValue } from '../../types';
import TextInput from './fields/TextInput';
import NumberInput from './fields/NumberInput';
import BooleanInput from './fields/BooleanInput';
import ChoiceInput from './fields/ChoiceInput';
import MultiChoiceInput from './fields/MultiChoiceInput';

// Every control gets the same props. None may branch on field_key or
// report_key, so a new report renders with no new code.
export interface FieldControlProps {
  field: ReportField;
  value: ReportFieldValue;
  onChange: (value: ReportFieldValue) => void;
}

const FIELD_COMPONENTS: Record<string, React.FC<FieldControlProps>> = {
  text: TextInput,
  number: NumberInput,
  boolean: BooleanInput,
  choice: ChoiceInput,
  multi_choice: MultiChoiceInput,
};

interface FieldRendererProps {
  field: ReportField;
  value: ReportFieldValue;
  error?: string;
  onChange: (value: ReportFieldValue) => void;
}

// An unknown type falls back to a plain text input rather than crashing, as
// the field reference promises (section 4).
const FieldRenderer: React.FC<FieldRendererProps> = ({ field, value, error, onChange }) => {
  const Control = FIELD_COMPONENTS[field.type];
  if (!Control) {
    console.warn(
      `[report-form] Unsupported field type "${field.type}" on field "${field.field_key}" — falling back to a text input.`
    );
  }
  const ResolvedControl = Control ?? TextInput;

  return (
    <div className={`report-field ${error ? 'has-error' : ''}`}>
      <label className="report-field-label">
        {field.field_label}
        {field.required && <span className="report-field-required">*</span>}
      </label>
      {field.help_text && <div className="report-field-help">{field.help_text}</div>}
      <ResolvedControl field={field} value={value} onChange={onChange} />
      {error && <div className="report-field-error">{error}</div>}
    </div>
  );
};

export default FieldRenderer;
